import type { ChatbotTypes, ChatbotTypesUpstream } from 'src/types/chatbot';
import type { DashboardConfig } from 'src/types/dashboard';
import type {
  ModuleOrPlanActionOrgUnit,
  ModulePermissionOrgUnit,
  PlanActionPermissionOrgUnit,
} from 'src/types/orgUnit';
import type { OrgTreeState } from 'src/components/common/OrgTree/types';
import { CHECKBOX_MODE } from 'src/components/common/OrgTree/types';

/**
 * Return the value of a flag form the org tree.
 */
export const getFlagInOrgTree = (
  orgTreeState: OrgTreeState,
  flag: keyof ModuleOrPlanActionOrgUnit,
) =>
  !!orgTreeState?.selectedOrgUnits.some(
    ({ id }) => orgTreeState.orgUnitMap[id]?.orgUnit[flag],
  );

/**
 * Check if a settings equals the setting in the org tree.
 */
export const equalsSettingInOrgTree = (
  orgTreeState: OrgTreeState,
  flag: keyof PlanActionPermissionOrgUnit | keyof ModulePermissionOrgUnit,
  value: string | number | boolean,
) =>
  !!orgTreeState?.selectedOrgUnits.some(({ id }) => {
    const orgUnit = orgTreeState.orgUnitMap[id]?.orgUnit;
    if (flag in orgUnit) {
      return orgUnit[flag as keyof typeof orgUnit] === value;
    }
    return false;
  });

/**
 * Get the configured selection mode for the org tree from the dashboard config
 */
export const getSelectionMode = (
  dashboardConfig: DashboardConfig | undefined,
) =>
  dashboardConfig && dashboardConfig.multiple_org_unit_selection
    ? CHECKBOX_MODE.MULTI
    : CHECKBOX_MODE.SINGLE;

/**
 * Get the chatbot type from the org tree state.
 */
export const getChatbotType = (
  dashboardConfig: DashboardConfig,
  orgTreeState: OrgTreeState,
): ChatbotTypes | undefined => {
  const checkChatbotType = (chatbotType: ChatbotTypesUpstream) =>
    dashboardConfig.show_chatbot === chatbotType &&
    equalsSettingInOrgTree(orgTreeState, 'show_chatbot', true);

  if (checkChatbotType('virtual_assistant')) {
    return 'virtualAssistant';
  }
  if (checkChatbotType('gpt')) {
    return 'openAi';
  }

  return undefined;
};

export const getRelativeValue = (
  absoluteValue: number | undefined,
  totalValue: number | undefined,
  decimalPrecision?: number,
) => {
  if (!totalValue || !absoluteValue) return 0;
  const relativeValue = (absoluteValue * 100) / totalValue;
  if (decimalPrecision === undefined) return relativeValue;
  return Number(relativeValue.toFixed(decimalPrecision));
};
